/**
 * The hero search's rotating example placeholder (M-3).
 *
 * Rails cycled real Hong Kong place names through the hero input — Sai Ying
 * Pun, Mei Foo Sun Chuen, Kingswood Villas — which is how a new visitor learns
 * that this box takes an estate or a district, not a listing id. The static
 * placeholder that replaced it teaches nothing.
 *
 * Two things it must not do:
 *
 *   * animate for someone who asked it not to — `prefers-reduced-motion`
 *     stops the rotation entirely, leaving the first example in place;
 *   * differ between server and client on the first paint — the index starts
 *     at 0 and only advances after mount, so hydration matches.
 *
 * It also stops while the user is typing: a placeholder is only visible in an
 * empty field, and mutating it under an active cursor is noise.
 *
 * The examples themselves come from the ui-strings SSOT's
 * `home.hero.searchExamples` list — see {@link useHeroSearchPlaceholder} for why
 * the COUNT is read from the catalog rather than stated here.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { useMediaQuery } from '@letsgethome/lib/react/use-media-query';
import type { LocalizedText } from '@letsgethome/types/branded';
import { useEffect, useMemo, useState } from 'react';

/** How long each example stays up. Long enough to read, short enough to cycle. */
const ROTATE_INTERVAL_MS = 3_000;

export interface RotatingPlaceholderOptions {
  /** The examples to cycle, in order. A single entry simply never rotates. */
  examples: readonly string[];
  /** Pause while the field has content (a placeholder is invisible then). */
  paused?: boolean;
}

/**
 * The example to show right now.
 *
 * Returns the first example on the server and on the first client render, so
 * the markup matches; rotation begins on the next interval tick.
 */
export function useRotatingPlaceholder({
  examples,
  paused = false,
}: RotatingPlaceholderOptions): string | undefined {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [index, setIndex] = useState(0);

  const shouldRotate = !prefersReducedMotion && !paused && examples.length > 1;

  useEffect(() => {
    if (!shouldRotate) return;
    const id = setInterval(() => {
      setIndex((current) => (current + 1) % examples.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [shouldRotate, examples.length]);

  // Guard the index against a shrinking example list (a locale with fewer
  // entries, or a mid-rotation prop change) rather than returning undefined.
  return examples.length > 0 ? examples[index % examples.length] : undefined;
}

/**
 * The hero search's placeholder: a rotating real place name, or `undefined` to
 * let `SearchBar` use its own default.
 *
 * Shared by both hero variants (the mobile `HeroSearchForm` and the desktop
 * `DockableHeroSearch`) so the two cannot show different examples. It named
 * `HeroSearch` here until that module was deleted: `HeroSection.searchSlot` was
 * optional with a `?? <HeroSearch />` fallback that its only caller always
 * passed past, so the module had been unrenderable for its whole life.
 *
 * HOW MANY examples there are is the CATALOG's answer, read through
 * `t.indices` — never a count stated here. This file used to carry a
 * `HERO_SEARCH_EXAMPLE_LIMIT = 8` "upper bound" against a five-entry manifest,
 * plus a filter meant to drop the three unanswered indices. The filter compared
 * against the NAMESPACE-RELATIVE key (`hero.searchExamples.5`) while react-intl
 * falls back to the FULL message id (`home.hero.searchExamples.5`), so it never
 * matched: the raw ids rendered as visible placeholder text on the live
 * homepage and browsers logged three MISSING_TRANSLATION warnings per paint.
 * Both the count and the filter are gone — the index tuple is fixed-length and
 * every index in it resolves, by construction.
 */
export function useHeroSearchPlaceholder(query: string): LocalizedText | undefined {
  const t = useTranslations('home');
  const examples = useMemo(
    () => t.indices('hero.searchExamples').map((i) => t.dyn(`hero.searchExamples.${i}`)),
    [t]
  );
  const current = useRotatingPlaceholder({
    examples,
    // A placeholder is invisible once the field has content; rotating it under
    // an active cursor is pure noise.
    paused: query.length > 0,
  });
  return current as LocalizedText | undefined;
}
